/**
 * AI builder chat: converse with the built-in builder agent, which designs and
 * creates an agent set for you. The set it builds streams back as a `set` event
 * and renders live in the right-hand panel; on close the created set is handed
 * back to the parent (to select + refresh the list).
 *
 * The set is shown to the user as an "Agent Team" — user-facing strings say
 * "team" while the code/API construct stays a "set" (see SetsPanel.js header).
 */
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Textarea from '@mui/joy/Textarea';
import IconButton from '@mui/joy/IconButton';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import AgentSetGraph from './AgentSetGraph';
import Markdown from '../common/Markdown';
import ErrorBoundary from '../common/ErrorBoundary';
import { startAgentChat, SET_BUILDER_AGENT_ID } from '../../api/chat';

// The test drawer pulls in the WebRTC widget; load it on demand so the heavy
// dependency stays out of the main bundle until a test is actually run.
const TestAgentDrawer = lazy(() => import('./TestAgentDrawer'));

/** The builder's offer to test a voice agent live in the browser. */
function TestOfferBubble({ message, onTest }) {
  const { name, done } = message;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start', maxWidth: '90%' }}>
      <Sheet variant="soft" color="neutral" sx={{ px: 1.5, py: 1, borderRadius: 'lg' }}>
        <Typography level="body-sm">Test <strong>{name}</strong> live in your browser?</Typography>
      </Sheet>
      {!done && (
        <Button size="sm" color="primary" startDecorator={<RecordVoiceOverIcon sx={{ fontSize: 16 }} />}
          onClick={onTest}>
          Test {name}
        </Button>
      )}
    </Box>
  );
}

const TOOL_LABEL = {
  create_agent_set: 'Creating the agent team…',
  update_agent_set: 'Updating the agent team…',
};

function Bubble({ role, text }) {
  if (role === 'tool' || role === 'system') {
    return (
      <Stack direction="row" justifyContent="center">
        <Chip size="sm" variant="soft" color={role === 'system' ? 'success' : 'neutral'} startDecorator={<BuildIcon sx={{ fontSize: 14 }} />}>
          {text}
        </Chip>
      </Stack>
    );
  }
  if (role === 'error') {
    return <Typography level="body-sm" color="danger" sx={{ textAlign: 'center' }}>{text}</Typography>;
  }
  const isUser = role === 'user';
  return (
    <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
      <Sheet
        variant={isUser ? 'solid' : 'soft'}
        color={isUser ? 'primary' : 'neutral'}
        sx={{ px: 1.5, py: 1, borderRadius: 'lg', maxWidth: '85%', wordBreak: 'break-word' }}
      >
        {isUser
          ? <Typography level="body-sm" sx={{ color: 'inherit', whiteSpace: 'pre-wrap' }}>{text}</Typography>
          : <Markdown text={text} />}
      </Sheet>
    </Box>
  );
}

/** A question from the agent with clickable answer options. */
function QuestionBubble({ message, onAnswer }) {
  const { question, options = [], multiSelect, answered, id } = message;
  const [selected, setSelected] = useState([]);
  const toggle = (opt) => setSelected((s) => (s.includes(opt) ? s.filter((x) => x !== opt) : [...s, opt]));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start', maxWidth: '90%' }}>
      <Sheet variant="soft" color="neutral" sx={{ px: 1.5, py: 1, borderRadius: 'lg', whiteSpace: 'pre-wrap' }}>
        <Typography level="body-sm">{question}</Typography>
      </Sheet>
      {!answered && options.length > 0 && (multiSelect ? (
        <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {options.map((opt) => (
              <Chip key={opt} variant={selected.includes(opt) ? 'solid' : 'outlined'} color="primary"
                onClick={() => toggle(opt)} sx={{ cursor: 'pointer' }}>{opt}</Chip>
            ))}
          </Stack>
          <Button size="sm" disabled={!selected.length} onClick={() => onAnswer(selected.join(', '), id)}>
            Send{selected.length ? ` (${selected.length})` : ''}
          </Button>
        </Stack>
      ) : (
        <Stack spacing={0.5} sx={{ alignItems: 'stretch', minWidth: 200 }}>
          {options.map((opt) => (
            <Button key={opt} size="sm" variant="outlined" color="primary"
              onClick={() => onAnswer(opt, id)} sx={{ justifyContent: 'flex-start' }}>{opt}</Button>
          ))}
        </Stack>
      ))}
    </Box>
  );
}

export default function SetBuilderChat({ onClose, onSet, setError, initialSet = null, initialTestResult = null }) {
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState('connecting'); // connecting | thinking | idle | error
  const [input, setInput] = useState('');
  const [liveSet, setLiveSet] = useState(initialSet);
  const [activeTest, setActiveTest] = useState(null); // {toolUseId, agentId, name} while a test drawer is open
  const editing = !!initialSet;
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);
  const latestSetRef = useRef(initialSet);

  const append = useCallback((m) => setMessages((prev) => [...prev, m]), []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, status]);

  useEffect(() => {
    let closed = false;
    startAgentChat(SET_BUILDER_AGENT_ID, {
      set: initialSet || undefined,
      testResult: initialTestResult || undefined,
      onOpen: () => !closed && setStatus('idle'),
      onClose: () => !closed && setStatus((s) => (s === 'error' ? s : 'idle')),
      onError: () => !closed && setStatus('error'),
      onMessage: (msg) => {
        if (closed) return;
        switch (msg.type) {
          case 'agent':
            if (msg.text) append({ role: 'agent', text: msg.text });
            break;
          case 'status':
            setStatus(msg.state === 'thinking' ? 'thinking' : 'idle');
            break;
          case 'tool_call':
            (msg.calls || []).forEach((c) => {
              const text = c.mcp
                ? `Consulting Aplisay docs (${c.name})…`
                : (TOOL_LABEL[c.name] || `Running ${c.name}…`);
              // Collapse consecutive tool-activity chips into one updating line
              // (the builder may make several MCP reads per turn).
              setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last && last.role === 'tool') return [...prev.slice(0, -1), { role: 'tool', text }];
                return [...prev, { role: 'tool', text }];
              });
            });
            break;
          case 'set':
            setLiveSet(msg.set);
            latestSetRef.current = msg.set;
            onSet && onSet(msg.set);
            append({ role: 'system', text: `Team “${msg.set?.name || 'untitled'}” saved (${msg.set?.agents?.length ?? 0} agents)` });
            break;
          case 'question':
            append({
              role: 'question',
              id: msg.id,
              question: msg.question,
              options: msg.options || [],
              multiSelect: !!msg.multiSelect,
              answered: false,
            });
            setStatus('idle'); // paused for the user — no spinner
            break;
          case 'test':
            append({
              role: 'test-offer',
              id: msg.id, // the test_agent tool-use id; the result resumes this turn
              agentId: msg.agentId,
              name: msg.name || msg.label,
              label: msg.label,
              done: false,
            });
            setStatus('idle'); // paused until the user runs the test
            break;
          case 'turn_complete':
            setStatus('idle');
            break;
          case 'error':
            append({ role: 'error', text: msg.message || 'Something went wrong' });
            setStatus('idle');
            break;
          default:
            break;
        }
      },
    })
      .then((session) => { if (closed) session.close(); else sessionRef.current = session; })
      .catch((err) => {
        setStatus('error');
        setError?.(`Could not start the set builder: ${err?.response?.data?.message || err.message}`);
      });

    return () => {
      closed = true;
      sessionRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = useCallback((text, questionId) => {
    if (!text || !sessionRef.current || status === 'connecting') return;
    if (questionId != null) {
      setMessages((prev) => prev.map((m) => (m.id === questionId ? { ...m, answered: true } : m)));
    }
    append({ role: 'user', text });
    sessionRef.current.send(text);
    setStatus('thinking');
  }, [status, append]);

  const send = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    submit(text);
    setInput('');
  }, [input, submit]);

  // The builder offered a live test; open the widget for that agent.
  const openTest = useCallback((m) => {
    setActiveTest({ toolUseId: m.id, agentId: m.agentId, name: m.name });
  }, []);

  // The test finished: mark the offer done and send the raw result JSON back as the
  // tool result so the builder's paused turn resumes and diagnoses it.
  const submitTestResult = useCallback((toolUseId, data) => {
    setMessages((prev) => prev.map((m) => (m.id === toolUseId ? { ...m, done: true } : m)));
    setActiveTest(null);
    if (!sessionRef.current) return;
    const note = data?.ok
      ? 'Ran the test — sending the transcript and call logs for review.'
      : `Test ended without a recorded call (${data?.reason || 'cancelled'}).`;
    append({ role: 'user', text: note });
    sessionRef.current.send(JSON.stringify(data || { ok: false, reason: 'cancelled' }));
    setStatus('thinking');
  }, [append]);

  const close = useCallback(() => {
    sessionRef.current?.close();
    onClose?.(latestSetRef.current);
  }, [onClose]);

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, height: { md: 600 } }}>
      {/* Chat */}
      <Sheet variant="outlined" sx={{ flex: 1, minWidth: 0, borderRadius: 'sm', display: 'flex', flexDirection: 'column', minHeight: 420 }}>
        <Stack direction="row" sx={{ p: 1.5, alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <AutoAwesomeIcon color="primary" />
            <Typography level="title-md">{editing ? 'Edit a team with AI' : 'Build a team with AI'}</Typography>
            {status === 'connecting' && <Chip size="sm" variant="soft">connecting…</Chip>}
            {status === 'error' && <Chip size="sm" variant="soft" color="danger">disconnected</Chip>}
          </Stack>
          <IconButton size="sm" variant="plain" onClick={close} aria-label="Close builder"><CloseIcon /></IconButton>
        </Stack>

        <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {messages.map((m, i) => {
            if (m.role === 'question') return <QuestionBubble key={i} message={m} onAnswer={submit} />;
            if (m.role === 'test-offer') return <TestOfferBubble key={i} message={m} onTest={() => openTest(m)} />;
            return <Bubble key={i} role={m.role} text={m.text} />;
          })}
          {status === 'thinking' && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'text.tertiary' }}>
              <CircularProgress size="sm" />
              <Typography level="body-xs">thinking…</Typography>
            </Stack>
          )}
        </Box>

        <Stack direction="row" spacing={1} sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Textarea
            minRows={1}
            maxRows={4}
            placeholder={editing ? 'Describe the change you want…' : 'Describe the team of agents you want…'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            disabled={status === 'connecting' || status === 'error'}
            sx={{ flex: 1 }}
          />
          <IconButton variant="solid" color="primary" onClick={send} disabled={!input.trim() || status === 'connecting'} aria-label="Send">
            <SendIcon />
          </IconButton>
        </Stack>
      </Sheet>

      {/* Live graph */}
      <Sheet variant="outlined" sx={{ flex: 1.2, minWidth: 0, borderRadius: 'sm', p: 1.5, display: 'flex', flexDirection: 'column' }}>
        {liveSet ? (
          <>
            <Stack direction="row" sx={{ mb: 1, alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography level="title-sm" noWrap sx={{ minWidth: 0 }}>{liveSet.name || 'Agent team'}</Typography>
              <Chip size="sm" variant="soft" color="success" startDecorator={<CheckCircleIcon sx={{ fontSize: 14 }} />}>
                Saved
              </Chip>
            </Stack>
            <Box sx={{ flex: 1, minHeight: 360 }}>
              <ErrorBoundary resetKey={liveSet?.agents?.length} title="Couldn’t draw this team — but it’s saved.">
                <AgentSetGraph set={liveSet} height={520} />
              </ErrorBoundary>
            </Box>
            {/* Kept outside the boundary so the set is always openable even if the graph fails. */}
            <Button size="sm" variant="solid" sx={{ mt: 1, alignSelf: 'flex-end' }} onClick={close}>Open in Builder</Button>
          </>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, color: 'text.tertiary', textAlign: 'center', minHeight: 360 }}>
            <AutoAwesomeIcon sx={{ fontSize: 40 }} />
            <Typography level="body-sm" sx={{ maxWidth: 320 }}>
              Tell the builder what you need. The agent team it designs will appear here as a graph as it’s created.
            </Typography>
          </Box>
        )}
      </Sheet>

      {activeTest && (
        <ErrorBoundary
          resetKey={activeTest.toolUseId}
          onError={() => submitTestResult(activeTest.toolUseId, { ok: false, reason: 'widget error' })}
        >
          <Suspense fallback={null}>
            <TestAgentDrawer
              key={activeTest.toolUseId}
              agent={{ id: activeTest.agentId, name: activeTest.name }}
              onResult={(data) => submitTestResult(activeTest.toolUseId, data)}
              onClose={() => setActiveTest(null)}
              setError={setError}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </Box>
  );
}
